import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Snackbar,
  Alert,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import axiosInstance from "../common/axios"; 
const backendUrl = process.env.REACT_APP_BACKEND_URL;
// Small helper to map server page result shape
const normalizeResponse = (res) => {
  // Expected: { content: [...], totalElements: number }
  if (!res) return { rows: [], total: 0 };
  const data = res.data || res;
  const rows = Array.isArray(data.content) ? data.content : data;
  const total = data.totalElements ?? data.total ?? (Array.isArray(data) ? data.length : 0);
  return { rows, total };
};

const emptyShipper = () => ({
  id: null,
  name: "",
  dob: "",
  licenseNumber: "",
  email: "",
  contactNumber: "",
  additionalContact: "",
  emergencyContact: "",
  homeAddress: {
    street1: "",
    street2: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  },
  city: "",
  state: "",
  country: "",
  homeZones: [], // array of { id, name } optionally
  vehicleType: "",
  vehicleTypeId: null,
  userId: null,
});

const ShipperManager = () => {
  // grid state
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // dialogs
  const [openCreateEdit, setOpenCreateEdit] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyShipper());
  const [openView, setOpenView] = useState(false);
  const [viewShipper, setViewShipper] = useState(null);

  // notifications
  const [snack, setSnack] = useState({ open: false, severity: "success", message: "" });

  // fetcher
  const fetchShippers = useCallback(async (p = page, size = pageSize) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`${backendUrl}/api/shippers?page=${p}&size=${size}`);
      const { rows: dataRows, total } = normalizeResponse(res);
      setRows(dataRows);
      setTotalRows(total);
    } catch (err) {
      console.error("Error fetching shippers", err);
      setSnack({ open: true, severity: "error", message: "Failed to load shippers" });
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchShippers(page, pageSize);
  }, [fetchShippers, page, pageSize]);

  // open create dialog
  const handleOpenCreate = () => {
    setForm(emptyShipper());
    setIsEditing(false);
    setOpenCreateEdit(true);
  };

  // open edit dialog
  const handleEdit = (shipper) => {
    // normalize shape if homeZones present as objects
    const copy = {
      ...shipper,
      dob: shipper.dob ? shipper.dob : "",
      homeAddress: shipper.homeAddress || emptyShipper().homeAddress,
      homeZones: (shipper.homeZones || []).map((z) =>
        typeof z === "object" ? { id: z.id, name: z.name } : { id: z }
      ),
    };
    setForm(copy);
    setIsEditing(true);
    setOpenCreateEdit(true);
  };

  // open view dialog
  const handleView = (shipper) => {
    setViewShipper(shipper);
    setOpenView(true);
  };

  // approve / deny
  const handleApprove = async (id) => {
    if (!window.confirm("Approve this shipper?")) return;
    try {
      await axiosInstance.post(`${backendUrl}/api/shippers/${id}/approve`);
      setSnack({ open: true, severity: "success", message: "Approved" });
      fetchShippers(page, pageSize);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: "error", message: "Approve failed" });
    }
  };

  const handleDeny = async (id) => {
    if (!window.confirm("Deny this shipper?")) return;
    try {
      await axiosInstance.post(`${backendUrl}/api/shippers/${id}/deny`);
      setSnack({ open: true, severity: "success", message: "Denied" });
      fetchShippers(page, pageSize);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, severity: "error", message: "Deny failed" });
    }
  };

  // save create or update
  const handleSave = async () => {
    // simple validation
    if (!form.name || !form.contactNumber) {
      setSnack({ open: true, severity: "warning", message: "Please enter name and contact number" });
      return;
    }

    try {
      if (isEditing && form.id) {
        await axiosInstance.put(`${backendUrl}/api/shippers/${form.id}`, form);
        setSnack({ open: true, severity: "success", message: "Updated shipper" });
      } else {
        await axiosInstance.post(`${backendUrl}/api/shippers`, form);
        setSnack({ open: true, severity: "success", message: "Created shipper" });
      }
      setOpenCreateEdit(false);
      fetchShippers(0, pageSize);
      setPage(0);
    } catch (err) {
      console.error("Save error", err);
      const message = err?.response?.data?.message || "Save failed";
      setSnack({ open: true, severity: "error", message });
    }
  };

  // form change helpers
  const handleFormChange = (field, value) => setForm((s) => ({ ...s, [field]: value }));
  const handleAddressChange = (field, value) =>
    setForm((s) => ({ ...s, homeAddress: { ...s.homeAddress, [field]: value } }));

  // DataGrid columns
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 150 },
    { field: "email", headerName: "Email", width: 200 },
    { field: "contactNumber", headerName: "Contact", width: 140 },
    {
      field: "city",
      headerName: "City",
      width: 130,
      valueGetter: (params) => params.row.city || params.row.homeAddress?.city || "-",
    },
    {
      field: "userId",
      headerName: "User ID",
      width: 110,
    },
          {
      field: "isApproved",
      headerName: "Approved",
      width: 110,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleView(params.row)} title="View">
            <VisibilityIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleEdit(params.row)} title="Edit">
            <EditIcon />
          </IconButton>
              {!params.row.isApproved && (
                  <>
          <Button
            size="small"
            color="success"
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => handleApprove(params.row.id)}
          >
            Approve
          </Button>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={() => handleDeny(params.row.id)}
          >
            Deny
          </Button>
                  </>   
                  
              )}
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Shippers</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Create Shipper
          </Button>
        </Box>

        <div style={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            rowCount={totalRows}
            page={page}
            pageSize={pageSize}
            paginationMode="server"
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            getRowId={(row) => row.id}
            disableSelectionOnClick
          />
        </div>
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={openCreateEdit} onClose={() => setOpenCreateEdit(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? "Edit Shipper" : "Create Shipper"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField label="Name" value={form.name} onChange={(e) => handleFormChange("name", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Email" value={form.email} onChange={(e) => handleFormChange("email", e.target.value)} fullWidth />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField label="Contact Number" value={form.contactNumber} onChange={(e) => handleFormChange("contactNumber", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Additional Contact" value={form.additionalContact} onChange={(e) => handleFormChange("additionalContact", e.target.value)} fullWidth />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField label="License No." value={form.licenseNumber} onChange={(e) => handleFormChange("licenseNumber", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Vehicle Type" value={form.vehicleType} onChange={(e) => handleFormChange("vehicleType", e.target.value)} fullWidth />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <Typography variant="subtitle2">Home Address</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Street 1" value={form.homeAddress?.street1 || ""} onChange={(e) => handleAddressChange("street1", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Street 2" value={form.homeAddress?.street2 || ""} onChange={(e) => handleAddressChange("street2", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="City" value={form.homeAddress?.city || ""} onChange={(e) => handleAddressChange("city", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="State" value={form.homeAddress?.state || ""} onChange={(e) => handleAddressChange("state", e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Country" value={form.homeAddress?.country || ""} onChange={(e) => handleAddressChange("country", e.target.value)} fullWidth />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>{isEditing ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={openView} onClose={() => setOpenView(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Shipper Details</DialogTitle>
        <DialogContent>
          {!viewShipper ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Name</Typography>
              <Typography paragraph>{viewShipper.name}</Typography>

              <Typography variant="subtitle2">Email</Typography>
              <Typography paragraph>{viewShipper.email}</Typography>

              <Typography variant="subtitle2">Contact</Typography>
              <Typography paragraph>{viewShipper.contactNumber}</Typography>

              <Typography variant="subtitle2">Address</Typography>
              <Typography paragraph>
                {viewShipper.homeAddress?.street1} {viewShipper.homeAddress?.street2} <br />
                {viewShipper.homeAddress?.city}, {viewShipper.homeAddress?.state} - {viewShipper.homeAddress?.postalCode}
              </Typography>

              <Typography variant="subtitle2">Zones</Typography>
              <Typography paragraph>
                {(viewShipper.homeZones || []).map((z) => (typeof z === "object" ? z.name : z)).join(", ")}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert onClose={() => setSnack({ ...snack, open: false })} severity={snack.severity} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ShipperManager;